'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui';
import { cn } from '@workspace/ui/lib';
import { ThemeToggle } from '@workspace/ui/components/custom/menu/theme-toggle';
import {
  AppTopHeader,
  appTopHeaderActionSurfaceClassName,
} from '@workspace/ui/components/layout/app-top-header';
import {
  AppTopHeaderHelpMenu,
  HELP_DESK_URL,
} from '@workspace/ui/components/layout/app-top-header-help-desk';
import { AppTopHeaderProfileMenu } from '@workspace/ui/components/layout/app-top-header-profile-menu';
import { CONTRACTOR_ROLE_VALUES } from '@workspace/ui/lib';
import { Bell, CircleHelp, ClipboardList, LogOut, Settings, SquarePlus, User } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { logout } from '@/actions/mixed/auth';
import { getAllNotifications } from '@/actions/notification';
import { ApplicationHeaderPortalMount } from '@/components/layout/application-header-context';
import { OrganisationSwitcher } from '@/components/layout/organisation-switcher';
import NotificationsWrapper from '@/components/notifications/notifications-wrapper';
import {
  useIsB2CFree,
  useIsB2CStandard,
  useIsContractor,
  useIsGigWorker,
  useIsIndividualCraft,
} from '@/hooks/use-contractor';
import { useIsEmployer, useIsTeamsGlobalAdmin } from '@/hooks/use-employer';
import { useRoles } from '@/hooks/use-roles';
import { getEorHref, getEorSupportRequestsHref } from '@/lib/eor-app-url';
import { useAuthStore } from '@/stores/authStore';
import { useNpsStore } from '@/stores/npsStore';

function getInitials(firstName?: string, lastName?: string): string {
  const initials =
    `${firstName?.[0]?.toUpperCase() ?? ''}${lastName?.[0]?.toUpperCase() ?? ''}`.trim();

  return initials || 'NT';
}

function HeaderNotifications() {
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => getAllNotifications('in-app'),
    refetchOnWindowFocus: true,
  });

  const unreadCount = data?.data?.filter((notification) => !notification.is_read).length ?? 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            appTopHeaderActionSurfaceClassName,
            'relative flex size-[30px] items-center justify-center rounded-full'
          )}
          aria-label={'Notifications'}
        >
          <Bell className="size-4" />
          {unreadCount > 0 && (
            <div
              className="bg-red-500 absolute top-0 right-0 size-2 rounded-full"
              aria-hidden="true"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={12}
        className="bg-grey-0 dark:bg-grey-1000 z-50 w-[495px] rounded-4xl px-8 py-12"
        data-notifications-popover="true"
      >
        <NotificationsWrapper />
      </PopoverContent>
    </Popover>
  );
}

export function WalletAppHeader() {
  const pathname = usePathname();
  const user = useAuthStore((state) => state.user);

  const isEmployerUser = useIsEmployer();
  const isContractorUser = useIsContractor();
  const isGigWorkerUser = useIsGigWorker();
  const isIndividualCraftUser = useIsIndividualCraft();
  const isB2CStandardUser = useIsB2CStandard();
  const isB2CFreeUser = useIsB2CFree();
  const isGlobalAdmin = useIsTeamsGlobalAdmin();
  const { hasAnyRole } = useRoles();
  const hasContractorRole = hasAnyRole(CONTRACTOR_ROLE_VALUES);
  const isContractor = isContractorUser || hasContractorRole;
  const canViewMyProfile = isEmployerUser && !isGlobalAdmin;
  const canViewProfile =
    !isEmployerUser &&
    (isContractor ||
      isGigWorkerUser ||
      isIndividualCraftUser ||
      isB2CStandardUser ||
      isB2CFreeUser);
  const userFirstName = user?.user.first_name;
  const userLastName = user?.user.last_name;
  const initials = getInitials(userFirstName, userLastName);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      useNpsStore.getState().reset();
      localStorage.removeItem('onboarding_check_completed');
      window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
    } catch {
      toast.error('Failed to logout, please try again later');
    }
  }, []);

  const helpMenuItems = useMemo(
    () => [
      {
        label: 'Request support',
        icon: SquarePlus,
        href: getEorSupportRequestsHref({ open: true, from: pathname }),
      },
      {
        label: 'My support requests',
        icon: ClipboardList,
        href: getEorSupportRequestsHref(),
      },
      {
        label: 'Help desk',
        icon: CircleHelp,
        href: HELP_DESK_URL,
        external: true,
      },
    ],
    [pathname]
  );

  const profileMenuItems = useMemo(
    () => [
      {
        label: 'Settings',
        icon: Settings,
        href: getEorHref('/settings'),
      },
      ...(canViewMyProfile
        ? [
            {
              label: 'My Profile',
              icon: User,
              href: getEorHref('/my-profile'),
            },
          ]
        : []),
      ...(canViewProfile
        ? [
            {
              label: 'My Profile',
              icon: User,
              href: getEorHref('/profile'),
            },
          ]
        : []),
      {
        label: 'Theme',
        icon: Settings,
        content: (
          <ThemeToggle
            className="text-neutral-200 hover:bg-grey-50 hover:text-neutral-200 dark:text-neutral-100 dark:hover:bg-grey-900 dark:hover:text-neutral-100 h-12 gap-3 rounded-xl px-3 font-medium [&>svg]:size-6!"
            iconClassName="size-6! stroke-2!"
            labelClassName="text-neutral-200 font-medium dark:text-neutral-100"
          />
        ),
      },
      {
        label: 'Logout',
        icon: LogOut,
        onSelect: handleLogout,
      },
    ],
    [canViewMyProfile, canViewProfile, handleLogout]
  );

  return (
    <AppTopHeader
      className="hidden min-[1199px]:flex"
      left={<ApplicationHeaderPortalMount />}
      organisationSwitcher={<OrganisationSwitcher />}
      helpDesk={<AppTopHeaderHelpMenu triggerLabel="Support" items={helpMenuItems} />}
      notifications={<HeaderNotifications />}
      profileMenu={<AppTopHeaderProfileMenu initials={initials} items={profileMenuItems} />}
    />
  );
}
